/**
 * Standard panel header — icon, title, subtitle, close.
 */
import React from 'react';
import { type as typeTokens, buttons, themes, cn } from './uiTokens';

interface PanelHeaderProps {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  icon?: string;
  onClose?: () => void;
  closeLabel?: string;
  className?: string;
  theme?: keyof typeof themes;
}

const PanelHeader: React.FC<PanelHeaderProps> = ({
  title,
  subtitle,
  eyebrow,
  icon,
  onClose,
  closeLabel = 'Close',
  className,
  theme = 'default',
}) => {
  const isSoc = theme === 'soc';

  return (
  <header
    className={cn(
      'flex items-center justify-between px-4 md:px-5 py-4 border-b shrink-0',
      isSoc ? 'border-green-900/50' : 'border-slate-800',
      className,
    )}
  >
    <div className={cn('flex min-w-0', isSoc ? 'items-center gap-4' : 'items-start gap-3')}>
      {icon && (
        <span
          className={cn(
            'shrink-0',
            isSoc
              ? 'w-12 h-12 border border-green-500 flex items-center justify-center bg-green-900/20 text-2xl'
              : 'text-2xl',
          )}
          aria-hidden
        >
          {icon}
        </span>
      )}
      <div className="min-w-0">
        {eyebrow && (
          <p className={isSoc ? 'text-xs text-green-700 uppercase tracking-widest' : typeTokens.eyebrowAccent}>
            {eyebrow}
          </p>
        )}
        <h2 className={isSoc ? 'text-2xl font-bold text-green-500 tracking-tighter' : typeTokens.title}>{title}</h2>
        {subtitle && (
          <p
            className={cn(
              isSoc ? 'text-xs text-green-700 uppercase tracking-widest' : typeTokens.eyebrow,
              'mt-0.5 normal-case tracking-wide',
            )}
          >
            {subtitle}
          </p>
        )}
      </div>
    </div>
    {onClose && (
      <button
        type="button"
        onClick={onClose}
        className={cn(
          isSoc && buttons.ghost,
          isSoc
            ? 'text-green-600 hover:text-green-400 border-green-800 hover:bg-green-900/20 uppercase text-xs tracking-widest'
            : buttons.icon,
        )}
        aria-label={closeLabel}
      >
        {isSoc ? closeLabel : '✕'}
      </button>
    )}
  </header>
  );
};

export default PanelHeader;